import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckIcon, ChevronDownIcon, MapPinIcon, SearchIcon, ShoppingBagIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useSite } from "../context/SiteContext";
import { locations, type LocationId } from "../data/locations";
import { menus, sides } from "../data/menuData";
export function MenuPage({
  location: locationProp
}: {
  location?: LocationId;
}) {
  const {
    locationId
  } = useParams();
  const id: LocationId = locationProp ?? (locationId === "victor" ? "victor" : "driggs");
  const location = locations[id];
  const menu = menus[id];
  const {
    t,
    setSelectedLocation
  } = useSite();
  const [query, setQuery] = useState("");
  const [sidesOpen, setSidesOpen] = useState(false);
  useEffect(() => {
    setSelectedLocation(id);
    document.title = `${location.officialName} Menu | Agave Family`;
  }, [id, location.officialName, setSelectedLocation]);
  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return menu.map(category => ({
      ...category,
      items: category.items.filter(item => `${item.name} ${item.description}`.toLowerCase().includes(normalizedQuery))
    })).filter(category => category.items.length);
  }, [menu, query]);
  const format = (price: number) => `$${price.toFixed(2)}`;
  const categoryId = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return <>
      <section className="relative overflow-hidden bg-cocoa px-4 py-14 text-ivory sm:px-6 sm:py-20" data-id="element-438" data-mp="1el4vc3ck3n3s">
        <div className="relative mx-auto max-w-7xl" data-id="element-439" data-mp="1il5ajq7hc2bw">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cocoa/60 px-4 py-2 text-xs font-bold uppercase tracking-[.2em] text-gold backdrop-blur" data-id="element-440" data-mp="1nltax2hllvis">
            <CheckIcon className="h-4 w-4" data-id="element-441" data-mp="2z06v9xd57su2" />
            {t(`You are viewing the ${id === "driggs" ? "Driggs" : "Victor"} menu`, `Estás viendo el menú de ${id === "driggs" ? "Driggs" : "Victor"}`)}
          </p>
          <h1 className="max-w-4xl font-serif text-4xl font-semibold leading-none text-ivory sm:text-6xl lg:text-7xl" data-id="element-442" data-mp="uwlu3zroqx0t">
            {location.officialName}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-ivory/80" data-id="element-443" data-mp="13ylb4s2pa13r">
            {t("Authentic Mexican cuisine, prepared with traditional techniques and the warmth of family hospitality.", "Cocina mexicana auténtica, preparada con técnicas tradicionales y la calidez de la hospitalidad familiar.")}
          </p>
          <p className="mt-3 text-sm text-ivory/75" data-id="element-444" data-mp="l7bzod9dd29j">
            {location.address} ·{" "}
            <a className="underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold" href={location.phoneHref} data-id="element-445" data-mp="3tmvpf2au02ju">
              {location.phone}
            </a>
          </p>
          <div className="mt-6 flex flex-wrap gap-3" data-id="element-446" data-mp="245qdpg1kh9ku">
            <Link to={`/menus/${id === "driggs" ? "victor" : "driggs"}`} className="inline-flex min-h-11 items-center rounded-full border border-gold/40 px-5 text-sm font-bold transition-colors hover:bg-gold/10" data-id="element-447" data-mp="244yja8jphhw5">
              {t(`Switch to ${id === "driggs" ? "Victor" : "Driggs"}`, `Cambiar a ${id === "driggs" ? "Victor" : "Driggs"}`)}
            </Link>
            <a href={location.directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 rounded-full bg-gold px-5 text-sm font-bold text-cocoa transition-colors hover:bg-sand" data-id="element-448" data-mp="2rvc9rumn78om">
              <MapPinIcon className="h-4 w-4" data-id="element-449" data-mp="12ge7s6wx5llf" />
              {t("Directions", "Cómo llegar")}
            </a>
          </div>
        </div>
      </section>

      <nav className="sticky top-[72px] z-30 border-b border-sand/50 bg-ivory/95 shadow-sm backdrop-blur" aria-label={t("Menu categories", "Categorías del menú")} data-id="element-450" data-mp="17g926kfwktb8">
        <div className="mx-auto flex max-w-7xl gap-3 overflow-x-auto px-4 py-3 sm:px-6" data-id="element-451" data-mp="1hxvzoi1lkp28">
          <label className="relative min-w-[250px] flex-none" data-id="element-452" data-mp="1cz1jlnv0lelt">
            <SearchIcon className="absolute left-4 top-3 h-5 w-5 text-charcoal/45" data-id="element-453" data-mp="1cx8c66ckuqcr" />
            <span className="sr-only" data-id="element-454" data-mp="18zand4mpy69w">{t("Search menu", "Buscar en el menú")}</span>
            <input value={query} onChange={event => setQuery(event.target.value)} placeholder={t("Search dishes or ingredients", "Buscar platillos o ingredientes")} className="min-h-11 w-full rounded-full border border-sand bg-white py-2 pl-11 pr-4 text-sm text-charcoal placeholder:text-charcoal/45 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20" data-id="element-455" data-mp="25mp1gv4whr4r" />
          </label>
          {menu.map(category => <a key={category.name} href={`#${categoryId(category.name)}`} className="inline-flex min-h-11 flex-none items-center rounded-full px-4 text-sm font-bold text-cocoa transition-colors hover:bg-terracotta/10 hover:text-terracotta" data-id="element-456" data-mp="g80sl3viqrbh">
              {category.name}
            </a>)}
        </div>
      </nav>

      <section className="bg-ivory px-4 py-12 sm:px-6 sm:py-16" data-id="element-457" data-mp="35obw7ohk3jc0">
        <div className="mx-auto max-w-7xl" data-id="element-458" data-mp="35pn5g3trtjai">
          {filtered.length ? <div className="space-y-16" data-id="element-459" data-mp="3amnaqcisqi0k">
              {filtered.map((category, categoryIndex) => <motion.section key={category.name} id={categoryId(category.name)} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.08
          }} transition={{
            duration: 0.28,
            delay: categoryIndex === 0 ? 0.05 : 0
          }} className="scroll-mt-44" data-id="element-460" data-mp="3fk5c9huhhuyj" data-mpi={categoryIndex}>
                  <div className="mb-6 rounded-2xl border border-gold/25 bg-cocoa p-6 sm:p-8" data-id="element-461" data-mp="2xjaxnhkdav5h">
                    <p className="text-xs font-bold uppercase tracking-[.24em] text-gold" data-id="element-462" data-mp="2skg7fuk2m4bc">
                      {t("From our kitchen", "De nuestra cocina")}
                    </p>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between" data-id="element-463" data-mp="36hwcb1mpxq0z">
                      <h2 className="max-w-4xl font-serif text-4xl font-semibold leading-none text-ivory sm:text-5xl" data-id="element-464" data-mp="3gif4cjofsctz">
                        {category.name}
                      </h2>
                      <span className="text-xs font-bold uppercase tracking-widest text-ivory/70" data-id="element-465" data-mp="3bh7vsiegz9m9">
                        {category.items.length} {t("items", "platillos")}
                      </span>
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2" data-id="element-466" data-mp="32qttsf19vnn">
                    {category.items.map((item, itemIndex) => <motion.article key={`${category.name}-${item.name}`} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                amount: 0.15
              }} transition={{
                duration: 0.24,
                delay: Math.min(itemIndex * 0.02, 0.12)
              }} className="group overflow-hidden rounded-xl border border-sand/70 bg-white shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-gold/60 hover:shadow-md" data-id="element-467" data-mp="3ubefk9h6rkvq" data-mpi={itemIndex}>
                        <div className="flex min-w-0 flex-col p-4 sm:p-5" data-id="element-468" data-mp="3s0z8c837cvq">
                          <div className="flex items-start justify-between gap-4" data-id="element-469" data-mp="3l7zbg3y91yb4">
                            <h3 className="font-serif text-xl font-semibold leading-snug text-cocoa" data-id="element-470" data-mp="3q5hl5wk3chk6">{item.name}</h3>
                            <p className="shrink-0 font-serif text-lg font-semibold text-terracotta" aria-label={`${item.name}, ${format(item.price)}`} data-id="element-471" data-mp="3c7azdesf280v">
                              {format(item.price)}
                            </p>
                          </div>
                          <p className="mt-2 text-[15px] leading-6 text-charcoal/80" data-id="element-472" data-mp="3mp6chc1mbn6r">{item.description}</p>
                        </div>
                      </motion.article>)}
                  </div>
                </motion.section>)}
            </div> : <div className="rounded-3xl border border-sand bg-white p-12 text-center" data-id="element-473" data-mp="38xrwle30c23x">
              <h2 className="font-serif text-3xl text-cocoa" data-id="element-474" data-mp="38vykhj9mkpoj">{t("No menu items found", "No encontramos platillos")}</h2>
              <button onClick={() => setQuery("")} className="mt-5 min-h-11 rounded-full bg-cocoa px-6 font-bold text-ivory transition-colors hover:bg-terracotta" data-id="element-475" data-mp="2yzvmmplt3d7e">
                {t("Clear search", "Limpiar búsqueda")}
              </button>
            </div>}

          <section className="mt-16 overflow-hidden rounded-2xl border border-sand bg-white" data-id="element-476" data-mp="15ho6m83rphzk">
            <button onClick={() => setSidesOpen(!sidesOpen)} className="flex min-h-16 w-full items-center justify-between px-5 text-left font-serif text-2xl font-semibold text-cocoa sm:px-6" aria-expanded={sidesOpen} data-id="element-477" data-mp="1af6kmcwn6nef">
              {t("Sides", "Acompañamientos")}
              <ChevronDownIcon className={`h-5 w-5 text-terracotta transition-transform ${sidesOpen ? "rotate-180" : ""}`} data-id="element-478" data-mp="mron00qp4v3j" />
            </button>
            {sidesOpen && <div className="border-t border-sand p-5 sm:p-6" data-id="element-479" data-mp="vqjx3nwv2pc4">
                <div className="grid gap-x-10 md:grid-cols-2" data-id="element-480" data-mp="vp8p1bwduvf7">
                  {sides.map(item => <article key={item.name} className="border-b border-sand/70 py-4" data-id="element-481" data-mp="32e98saigk82">
                      <div className="flex items-start justify-between gap-4" data-id="element-482" data-mp="30jy25p1k6h6">
                        <h3 className="font-serif text-lg font-semibold text-cocoa" data-id="element-483" data-mp="7zo78wbyu47i">{item.name}</h3>
                        <strong className="font-serif text-base text-terracotta" data-id="element-484" data-mp="3neq6r9ob0woe">{format(item.price)}</strong>
                      </div>
                      <p className="mt-2 text-[15px] leading-6 text-charcoal/75" data-id="element-485" data-mp="ajqdmrrli7k">{item.description}</p>
                    </article>)}
                </div>
              </div>}
          </section>

          <div className="mt-10 rounded-2xl border border-sand bg-warmwhite p-6 text-sm leading-6 text-charcoal/70" data-id="element-486" data-mp="33rs886mhsi2a">
            <p data-id="element-487" data-mp="38qvdns6dwqas">
              {t("Menu items, prices, and availability may change. Please confirm current availability when ordering.", "Los platillos, precios y disponibilidad pueden cambiar. Confirma la disponibilidad al realizar tu pedido.")}
            </p>
          </div>

          <div className="mt-10 flex flex-col items-center rounded-3xl bg-cocoa px-6 py-12 text-center text-ivory" data-id="element-488" data-mp="2kyesv9v9ik7s">
            <ShoppingBagIcon className="h-9 w-9 text-gold" data-id="element-489" data-mp="2ig30hpw159in" />
            <h2 className="mt-5 font-serif text-4xl" data-id="element-490" data-mp="2ifkxmaev3x1r">{t(`Order from ${location.shortName}`, `Ordena en ${location.shortName}`)}</h2>
            <p className="mt-3 max-w-xl text-ivory/70" data-id="element-491" data-mp="2df5mlpi1z1my">
              {t("Call the restaurant to place an order and confirm current availability.", "Llama al restaurante para ordenar y confirmar la disponibilidad actual.")}
            </p>
            <a href={location.phoneHref} className="mt-7 inline-flex min-h-12 items-center rounded-full bg-gold px-7 font-bold text-cocoa transition-colors hover:bg-sand" data-id="element-492" data-mp="1zjbm9uvchklg">
              {t("Call", "Llamar")} {location.phone}
            </a>
          </div>
        </div>
      </section>
    </>;
}